"use client";

import type { HelpCenterAction } from "@/lib/support/help-center-data";

export const HELP_CENTER_BRIDGE_VERSION = "1.0";
const BRIDGE_RESPONSE_EVENT = "cribnosh-native-bridge-response";

type NativeWebViewBridge = {
  postMessage: (data: string) => void;
};

type WindowWithNativeWebView = Window & {
  ReactNativeWebView?: NativeWebViewBridge;
};

export type HelpCenterBridgeRequest<TPayload = Record<string, unknown>> = {
  version: typeof HELP_CENTER_BRIDGE_VERSION;
  type: HelpCenterAction;
  requestId: string;
  payload?: TPayload;
};

export type HelpCenterBridgeResponse = {
  version: typeof HELP_CENTER_BRIDGE_VERSION;
  requestId: string;
  type: HelpCenterAction;
  status: "ok" | "error";
  payload?: Record<string, unknown>;
  error?: string;
};

function createRequestId(): string {
  const random = Math.random().toString(36).slice(2, 10);
  return `help-${Date.now()}-${random}`;
}

function getNativeWebViewBridge(): NativeWebViewBridge | null {
  if (typeof window === "undefined") {
    return null;
  }

  const bridge = (window as WindowWithNativeWebView).ReactNativeWebView;
  if (!bridge || typeof bridge.postMessage !== "function") {
    return null;
  }

  return bridge;
}

export function isNativeBridgeAvailable(): boolean {
  return Boolean(getNativeWebViewBridge());
}

export async function sendHelpCenterBridgeRequest<TPayload extends Record<string, unknown>>(
  type: HelpCenterAction,
  payload?: TPayload,
  timeoutMs = 3500
): Promise<HelpCenterBridgeResponse> {
  const bridge = getNativeWebViewBridge();
  const requestId = createRequestId();
  const request: HelpCenterBridgeRequest<TPayload> = {
    version: HELP_CENTER_BRIDGE_VERSION,
    type,
    requestId,
    payload,
  };

  if (!bridge) {
    return {
      version: HELP_CENTER_BRIDGE_VERSION,
      requestId,
      type,
      status: "error",
      error: "Native bridge is unavailable",
    };
  }

  return new Promise((resolve) => {
    let settled = false;

    const cleanup = (timeoutId: ReturnType<typeof setTimeout>) => {
      window.removeEventListener(BRIDGE_RESPONSE_EVENT, onResponse as EventListener);
      clearTimeout(timeoutId);
    };

    const settle = (timeoutId: ReturnType<typeof setTimeout>, response: HelpCenterBridgeResponse) => {
      if (settled) return;
      settled = true;
      cleanup(timeoutId);
      resolve(response);
    };

    const onResponse = (event: Event) => {
      const customEvent = event as CustomEvent<HelpCenterBridgeResponse>;
      const detail = customEvent.detail;
      if (!detail || detail.requestId !== requestId) {
        return;
      }
      settle(timeoutId, detail);
    };

    const timeoutId = setTimeout(() => {
      settle(timeoutId, {
        version: HELP_CENTER_BRIDGE_VERSION,
        requestId,
        type,
        status: "error",
        error: "Native bridge request timed out",
      });
    }, timeoutMs);

    window.addEventListener(BRIDGE_RESPONSE_EVENT, onResponse as EventListener);
    bridge.postMessage(JSON.stringify(request));
  });
}
